import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

export type SegmentedSwitchOption<T extends string> = {
  value: T;
  label: string;
  icon?: ReactNode;
};

type SegmentedSwitchProps<T extends string> = {
  value: T;
  options: readonly SegmentedSwitchOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  'aria-label': string;
};

const useStyles = makeStyles({
  track: {
    position: 'relative',
    boxSizing: 'border-box',
    display: 'inline-grid',
    alignItems: 'stretch',
    width: 'max-content',
    height: '32px',
    padding: 0,
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    boxShadow: `inset 0 0 0 1px ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  trackFull: {
    display: 'grid',
    width: '100%',
    minWidth: 0,
  },
  trackDisabled: {
    opacity: 0.6,
  },
  option: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '100%',
    margin: 0,
    paddingLeft: '8px',
    paddingRight: '8px',
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: tokens.fontFamilyBase,
    lineHeight: tokens.lineHeightBase200,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  optionFull: {
    minWidth: 0,
  },
  optionSelected: {
    color: tokens.colorBrandForeground1,
  },
  optionDisabled: {
    cursor: 'default',
  },
  thumb: {
    position: 'absolute',
    top: '3px',
    bottom: '3px',
    left: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    transitionProperty: 'transform, width',
    transitionDuration: '0.15s',
    pointerEvents: 'none',
  },
});

const SegmentedSwitch = <T extends string>({
  value,
  options,
  onChange,
  disabled = false,
  fullWidth = false,
  'aria-label': ariaLabel,
}: SegmentedSwitchProps<T>) => {
  const styles = useStyles();
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState({ x: 3, width: 0 });
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const button = track.querySelectorAll('button')[selectedIndex];
      if (!button) return;
      setThumb({
        x: button.offsetLeft + 3,
        width: Math.max(0, button.offsetWidth - 6),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [selectedIndex, options.length, fullWidth]);

  const move = (event: KeyboardEvent<HTMLDivElement>, offset: number) => {
    event.preventDefault();
    const next =
      options[(selectedIndex + offset + options.length) % options.length];
    if (next) onChange(next.value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      ref={trackRef}
      className={mergeClasses(
        styles.track,
        fullWidth && styles.trackFull,
        disabled && styles.trackDisabled
      )}
      style={{
        gridTemplateColumns: fullWidth
          ? `repeat(${options.length}, minmax(0, 1fr))`
          : `repeat(${options.length}, max-content)`,
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          move(event, 1);
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          move(event, -1);
        }
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={mergeClasses(
              styles.option,
              fullWidth && styles.optionFull,
              selected && styles.optionSelected,
              disabled && styles.optionDisabled
            )}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.icon}
            <span>{option.label}</span>
          </button>
        );
      })}
      <span
        className={styles.thumb}
        style={{
          width: thumb.width,
          transform: `translateX(${thumb.x}px)`,
        }}
      />
    </div>
  );
};

export default SegmentedSwitch;
